import * as React from "react";
import { useState, useEffect } from "react";
import { Card, CardContent, Box } from "@mui/material";
import { Title } from "react-admin";
import { Appearance, loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements, ElementsConsumer, PaymentElement } from "@stripe/react-stripe-js";
import { useStateMachine } from "little-state-machine";
import StripeFormPaymentIntent from "../components/stripeFormPaymentIntent";
import StripeFormSetupIntent from "../components/stripeFormSetupIntent";
// import { CheckoutForm } from "../components/stripeForm";
import { useCheckoutSubmit } from "../hooks/useCheckout";
import { useBasket } from "../basket/basketContext";
import { CheckoutTitle } from "./checkoutCustomer";
import Accordion, { AccordionProps } from "@mui/material/Accordion";
import { styled } from "@mui/material/styles";
import AccordionDetails from "@mui/material/AccordionDetails";
//import AccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CircularProgress from "@mui/material/CircularProgress";
import CartList from "../components/cartList";
import { useMutation, UseMutationOptions, UseMutationResult, useQuery, UseQueryResult } from "react-query";
import { ClientError } from "graphql-request";
import { __DEV__, PAGEVIEW, processENV } from "../api";
import { stripeElementAppearance } from "../themes";
import { addPageViewTrackingEvent } from "../components/trackingEvents";
import MessageConfirmModal from "../components/messageConfirmModal";

const AccordionSummary = styled((props: AccordionSummaryProps & { expandedaccordion: number }) => (
  <MuiAccordionSummary
    expandIcon={
      !!props.expandedaccordion ? (
        <RadioButtonCheckedIcon sx={{ fontSize: "20px", color: "#2bb573" }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: "20px", color: "#8193A8" }} />
      )
    }
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "#FFFFFF",
  flexDirection: "row-reverse",
  "&.MuiAccordionSummary-root": {
    borderRadius: "12px",
    padding: "24px 26px 24px 26px",
  },
  ".Mui-expanded&.MuiAccordionSummary-root": {
    padding: "24px 26px 0px 26px",
  },
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    // transform: "rotate(90deg)",
    // marginLeft: "6px",
    // marginRight: "6px",
  },
  "& .MuiAccordionSummary-content.Mui-expanded": {
    margin: "12px 0 12px 6px",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: "6px",
    color: "#8193A8",
    fontFamily: "Inter 24pt",
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 700,
  },
  ".Mui-expanded& .MuiAccordionSummary-content": {
    color: theme.palette.primary.main,
  },
}));

const BlueAccordion = styled((props: AccordionProps) => (
  <Accordion sx={{ borderColor: props.expanded ? "#231f20" : "#CDD4DC" }} {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "#FFFFFF",
  flexDirection: "row-reverse",
  "&.MuiAccordion-root": {
    border: "1px solid ",
    borderRadius: "12px",
    borderColor: "#CDD4DC",
    boxShadow: "none",
    marginBottom: "20px",
  },
  "&.MuiAccordion-root.Mui-expanded": {
    borderColor: theme.palette.primary.main,
  },
}));

const stripePromise = loadStripe(processENV.REACT_APP_STRIPE_CLIENT_SECRET || "");

const Payment = ({
  cartData,
  translation,
  setActiveStep,
  setMessageSnackBarOpen,
  setMessageSnackBar,
  payNowButtonRef,
  isLoadingPayNowConfirm,
  setIsLoadingPayNowConfirm,
}: {
  cartData: GQL.CheckoutOpen | GQL.CheckoutUpdate | null;
  translation: GQL_AEM.CheckoutTranslationModels | null;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setMessageSnackBarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMessageSnackBar: React.Dispatch<React.SetStateAction<string>>;
  payNowButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  isLoadingPayNowConfirm: boolean;
  setIsLoadingPayNowConfirm: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [stripeClientSecret, setStripeClientSecret] = useState("");
  const [paypalClientToken, setPaypalClientToken] = useState("");
  const [cart_has_subscription, setCart_has_subscription] = useState(false);
  // const mutationCheckoutSessionCreate = useCheckoutSessionCreate();
  // const mutationOrder = useOrder();
  // const mutationOrderSubmit = useOrderSubmit();
  const mutationCheckoutSubmit = useCheckoutSubmit();
  const { state } = useStateMachine();
  // const basket = useBasket();

  const [expandedAccordion, setExpandedAccordion] = React.useState<string>("");

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : "");
  };

  useEffect(() => {
    if (__DEV__) {
      console.log("StripePromise", stripePromise);
    }
    if (mutationCheckoutSubmit.isLoading === false) {
      mutationCheckoutSubmit.mutate(
        {
          checkoutId: state.data.checkoutId,
        },
        {
          onSuccess: (data) => {
            setPaypalClientToken(data.paypalClientToken ? data.paypalClientToken : "");
            setStripeClientSecret(data.stripeClientSecret ? data.stripeClientSecret : "");
            setCart_has_subscription(data.hasSubscription ? data.hasSubscription : false);
          },
        }
      );
    }

    addPageViewTrackingEvent(cartData, PAGEVIEW.CHECKOUT_REGULAR);

    return function cleanup() {
      if (__DEV__) {
        console.log("cleanup");
      }
    };
  }, []);

  const options: StripeElementsOptions = {
    clientSecret: stripeClientSecret,
    appearance: stripeElementAppearance,
    loader: "auto",
  };
  return (
    <div className="payment" style={{ margin: "20px 0px 0 0" }}>
      <>
        {/* <Title title="Playbrush Store" /> */}
        {/* <CardContent> */}

        <CheckoutTitle>{translation?.step_payment || "Payment"}</CheckoutTitle>
        {stripeClientSecret && mutationCheckoutSubmit.isLoading === false && (
          <>
            <Elements stripe={stripePromise} options={options}>
              {cartData !== null &&
                (cartData.amountTotal === 0 ? (
                  <StripeFormSetupIntent
                    payNowButtonRef={payNowButtonRef}
                    isLoadingPayNowConfirm={isLoadingPayNowConfirm}
                    setIsLoadingPayNowConfirm={setIsLoadingPayNowConfirm}
                    stripeClientSecret={stripeClientSecret}
                    setMessageSnackBarOpen={setMessageSnackBarOpen}
                    setMessageSnackBar={setMessageSnackBar}
                  />
                ) : cartData.hasSubscription ? (
                  <StripeFormPaymentIntent
                    payNowButtonRef={payNowButtonRef}
                    isLoadingPayNowConfirm={isLoadingPayNowConfirm}
                    setIsLoadingPayNowConfirm={setIsLoadingPayNowConfirm}
                    stripeClientSecret={stripeClientSecret}
                    setMessageSnackBarOpen={setMessageSnackBarOpen}
                    setMessageSnackBar={setMessageSnackBar}
                  />
                ) : (
                  <StripeFormPaymentIntent
                    payNowButtonRef={payNowButtonRef}
                    isLoadingPayNowConfirm={isLoadingPayNowConfirm}
                    setIsLoadingPayNowConfirm={setIsLoadingPayNowConfirm}
                    stripeClientSecret={stripeClientSecret}
                    setMessageSnackBarOpen={setMessageSnackBarOpen}
                    setMessageSnackBar={setMessageSnackBar}
                  />
                ))}
            </Elements>
          </>
        )}
        {mutationCheckoutSubmit.isLoading && (
          <Box
            sx={{
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              minHeight: "300px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {/* </CardContent> */}
        {cartData && <CartList cart={cartData} listType={"MARKETING"} translation={translation} />}
      </>
    </div>
  );
};

export default Payment;
